import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  fetchFavourites,
  setFavouritesRemote,
  mergeFavouritesRemote,
  ApiError,
} from '../api/authApi';
import { useAuthStore } from './authStore';

type FavouritesState = {
  /** The authoritative set of favourite symbol codes (raw codes like XXBTZUSD, not wsSymbols). */
  favourites: string[];
  /** True while the initial remote load is in flight. */
  loading: boolean;
  /** Last sync direction — 'server' once the server replaced our local copy. */
  syncedFromServer: boolean;
};

type FavouritesActions = {
  has: (symbol: string) => boolean;
  toggle: (symbol: string) => void;
  add: (symbol: string) => void;
  remove: (symbol: string) => void;
  /**
   * Load favourites from the server (if authenticated). If the local copy
   * has entries that the server doesn't, they're merged in (union).
   */
  syncFromServer: () => Promise<void>;
  /**
   * Push current local favourites to the server. Called after toggle/add/
   * remove when the user is authenticated, debounced so rapid clicks coalesce.
   */
  pushToServer: () => Promise<void>;
};

const MAX_FAVOURITES = 200;

function clean(symbols: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of symbols) {
    if (typeof s !== 'string') continue;
    const t = s.trim();
    if (!t || t.length > 64) continue;
    if (seen.has(t)) continue;
    seen.add(t);
    out.push(t);
    if (out.length >= MAX_FAVOURITES) break;
  }
  return out;
}

let pushTimer: ReturnType<typeof setTimeout> | null = null;
const PUSH_DEBOUNCE_MS = 400;

export const useFavouritesStore = create<FavouritesState & FavouritesActions>()(
  persist(
    (set, get) => ({
      favourites: [],
      loading: false,
      syncedFromServer: false,

      has: (symbol) => get().favourites.includes(symbol),

      toggle: (symbol) => {
        const current = get().favourites;
        const next = current.includes(symbol)
          ? current.filter((s) => s !== symbol)
          : clean([...current, symbol]);
        set({ favourites: next });
        schedulePush(get().pushToServer);
      },

      add: (symbol) => {
        const current = get().favourites;
        if (current.includes(symbol)) return;
        set({ favourites: clean([...current, symbol]) });
        schedulePush(get().pushToServer);
      },

      remove: (symbol) => {
        const current = get().favourites;
        if (!current.includes(symbol)) return;
        set({ favourites: current.filter((s) => s !== symbol) });
        schedulePush(get().pushToServer);
      },

      syncFromServer: async () => {
        if (!useAuthStore.getState().isAuthenticated) return;
        set({ loading: true });
        try {
          const localOnly = get().favourites;
          let remote: string[];
          if (localOnly.length > 0 && !get().syncedFromServer) {
            // Anon user had local favourites — merge into server on first sync.
            const res = await mergeFavouritesRemote(localOnly);
            remote = res.favourites;
          } else {
            const res = await fetchFavourites();
            remote = res.favourites;
          }
          set({ favourites: clean(remote), syncedFromServer: true });
        } catch (err) {
          // 401 → user probably logged out mid-sync; leave local state alone.
          if (!(err instanceof ApiError && err.status === 401)) {
            console.warn('Favourites sync failed:', err);
          }
        } finally {
          set({ loading: false });
        }
      },

      pushToServer: async () => {
        if (!useAuthStore.getState().isAuthenticated) return;
        try {
          await setFavouritesRemote(get().favourites);
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 401)) {
            console.warn('Favourites push failed:', err);
          }
        }
      },
    }),
    {
      name: 'lynx-favourites',
      version: 1,
      // Persist the list + sync flag; transient loading state stays in memory
      partialize: (state) => ({
        favourites: state.favourites,
        syncedFromServer: state.syncedFromServer,
      }),
    },
  ),
);

function schedulePush(fn: () => Promise<void>): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void fn();
  }, PUSH_DEBOUNCE_MS);
}
